import { formatNumber } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { Params } from '@angular/router';
import { SavingsPlanFacade } from '@zero/saving-plans/domain';
import { PushQuoteService } from '@zero/shared/ui-push';
import { AbstractDataService } from '@zero/shared/util-abstract-services';
import { CustomerStateFacade, PositionsFacade } from '@zero/shared/util-global-state';
import {
  Asset,
  BuyingPowerDetails,
  CombinedOrderRequest,
  OrderData,
  OrderPushData,
  OrderRequest,
  Position,
  Quote,
} from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, firstValueFrom, map, switchMap } from 'rxjs';
import { FormCache } from '../../entities/form-cache.entity';
import { MinimumQuantitySurcharge } from '../../entities/minimum-quantity-surcharge.entity';
import { OrderFormGroup } from '../../entities/order-form.entity';
import { OrderVolumeCalculation } from '../../entities/order-volume-calculation.entity';
import { MinimumQuantitySurchargeService } from '../minimum-quantity-surcharge/minimum-quantity-surcharge.service';
import { TradeFacade } from '../trade.facade';
import { TradingTimesService } from '../trading-times/trading-times.service';

@Injectable({
  providedIn: 'root',
})
export class TradeService {
  public asset = new BehaviorSubject<Asset | null>(null);
  public quote = new BehaviorSubject<Quote | null>(null);
  public buyingPower = new BehaviorSubject<number>(0);
  public orderVolume = new BehaviorSubject<number>(0);
  public tickingStopPrice = new BehaviorSubject<number>(0);
  readonly #buyingPowerDetails = new BehaviorSubject<BuyingPowerDetails>(null);
  readonly #cache = new BehaviorSubject<FormCache>(null);
  readonly #minimumQuantitySurcharge = new BehaviorSubject<MinimumQuantitySurcharge>(null);
  readonly #displaySpread = new BehaviorSubject<boolean>(false);

  public readonly asset$ = this.asset.asObservable();
  public readonly quote$ = this.quote.asObservable();
  public readonly buyingPower$ = this.buyingPower.asObservable();
  public readonly buyingPowerDetails$ = this.#buyingPowerDetails.asObservable();
  public readonly orderVolume$ = this.orderVolume.asObservable();
  public readonly cache$ = this.#cache.asObservable();
  public readonly minimumQuantitySurcharge$ = this.#minimumQuantitySurcharge.asObservable();
  public readonly displaySpread$ = this.#displaySpread.asObservable();
  public readonly tickingStopPrice$ = this.tickingStopPrice.asObservable();

  readonly #SPREAD_CACHE_KEY = 'displaySpread';
  readonly #positionsFacade = inject(PositionsFacade);
  readonly #dataService = inject(AbstractDataService);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #minimumQuantitySurchargeService = inject(MinimumQuantitySurchargeService);
  readonly #http = inject(HttpClient);
  readonly #tradingTimesService = inject(TradingTimesService);
  readonly #savingsPlanFacade = inject(SavingsPlanFacade);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #destroyRef = inject(DestroyRef);
  readonly #tradeFacade = inject(TradeFacade);

  constructor() {
    this.#shouldDisplaySpread() && this.#displaySpread.next(true);
  }

  public get cache(): FormCache {
    return this.#cache.value;
  }

  public loadHolidays = (): void => this.#tradingTimesService.initHolidays();

  public reset = (): void => {
    this.asset.value?.isin && this.#pushQuoteService.unsubscribe(this.asset.value.isin);
    this.asset.next(null);
    this.quote.next(null);
    this.buyingPower.next(0);
    this.#buyingPowerDetails.next(null);
    this.orderVolume.next(0);
    this.#minimumQuantitySurcharge.next(null);
  };

  public verifyOrder = (): void => {
    const orderRequest = this.#getOrderRequest();
    if (!orderRequest) {
      throw new Error('Order could not be verified');
    }

    this.#tradeFacade.verifyOrder(orderRequest);
  };

  public validateCombinedOrder = (): void => {
    const combinedOrderRequest = this.#getCombinedOrderValidationRequest();
    if (!combinedOrderRequest) {
      throw new Error('Combined Order could not be validated');
    }

    this.#tradeFacade.validateCombinedOrder(combinedOrderRequest);
  };

  public updateInstrument = (asset: Asset): void => {
    this.asset.next(asset);

    if (asset) {
      const { isin } = asset;
      this.#subscribeToPushQuote(isin);
      this.#subscribeToOrderVolume(asset);
    }
  };

  #shouldDisplaySpread(): boolean {
    return localStorage.getItem(this.#SPREAD_CACHE_KEY) === 'true';
  }

  public toggleSpread = (): void => {
    const displaySpread = !this.#displaySpread.value;
    localStorage.setItem(this.#SPREAD_CACHE_KEY, displaySpread.toString());
    this.#displaySpread.next(displaySpread);
  };

  public updateQuote = (quote: Quote): void => this.quote.next(quote);

  public loadBuyingPower = async (isin: string): Promise<void> => {
    combineLatest([this.#customerStateFacade.customerBaseData$, this.#dataService.loadBPDetails$(isin)])
      .pipe(
        filter((customerAccount) => !!customerAccount),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(([, bpDetails]) => {
        this.buyingPower.next(bpDetails?.buyingPower);
        this.#buyingPowerDetails.next(bpDetails);
      });
  };

  public updateOrderVolume = (data: OrderVolumeCalculation): void => this.orderVolume.next(this.#getOrderVolume(data));

  public getPositionInPortfolio = async (isin: string): Promise<Position> => {
    this.#positionsFacade.loadPositions(true);
    await firstValueFrom(this.#positionsFacade.positionsInitialLoaded$.pipe(filter((loaded) => !!loaded)));
    return firstValueFrom(this.#positionsFacade.getPositionByIsin$(isin));
  };

  public cacheForm = (form: FormGroup<OrderFormGroup>): void =>
    this.#cache.next({
      form,
      asset: this.asset.value,
    });

  #getOrderRequest(): OrderRequest {
    const { form, asset } = this.#cache.value;
    if (!form || !asset) {
      return null;
    }

    const { isin } = asset;
    let { value: execType } = form.get('execType');
    const { value: direction } = form.get('direction');
    const quantityForm = form.get('quantityForm');
    const optionsForm = form.get('optionsForm');
    const { value: quantity } = quantityForm.get('quantity');
    let { value: validToDate } = quantityForm.get('validToDate');

    const {
      stopLimitPrice,
      stopLoss,
      takeProfit,
      initialStopPrice,
      trailingAbs: trailingPriceAbs,
      trailingRel: trailingPricePct,
      trailingType,
    } = optionsForm.getRawValue();
    let { limitPrice, stopPrice } = optionsForm.getRawValue();

    const minimumQuantitySurcharge = this.#minimumQuantitySurcharge.value?.value ?? 0;

    switch (execType) {
      case 'stop':
      case 'stoplimit':
        if (stopLimitPrice && !isNaN(stopLimitPrice)) {
          execType = 'stoplimit';
          limitPrice = stopLimitPrice;
        }
        break;
      case 'oco':
        limitPrice = takeProfit;
        stopPrice = stopLoss;
        break;
      case 'tsl':
        stopPrice = initialStopPrice || this.tickingStopPrice?.value;
        break;
      default:
        break;
    }

    const isMarketBuy = execType === 'market' && direction === 'buy';
    /*
      - our API expects UTC date
      - we need system date here with timezone offset information.
      - the http client converts date to iso string
    */
    validToDate = isMarketBuy ? this.#tradingTimesService.getCurrentLocalDateTime() : validToDate;
    const customer = this.#dataService.getSelectedCustomer();

    return {
      customer,
      direction,
      execType,
      isin,
      limitPrice,
      minimumQuantitySurcharge,
      quantity,
      stopPrice,
      validToDate,
      trailingPriceAbs: trailingType === 'abs' ? trailingPriceAbs : null,
      trailingPricePct: trailingType === 'rel' ? trailingPricePct : null,
    } as OrderRequest;
  }

  #getCombinedOrderValidationRequest(): CombinedOrderRequest {
    const { form, asset } = this.#cache.value;
    if (!form || !asset) {
      return null;
    }

    const quantity = Number(form.get('quantityForm').get('quantity').value);
    const decimals = quantity.toString().split('.')[1]?.length ?? 0;
    const quantityWithoutFractions = Math.floor(quantity);
    const fractions = quantity - quantityWithoutFractions;

    return {
      customerId: this.#dataService.getSelectedCustomer(),
      isin: asset.isin,
      direction: form.get('direction').value,
      orders: [
        ...(quantityWithoutFractions ? [{ quantity: quantityWithoutFractions }] : []),
        { quantity: decimals > 0 ? Number(fractions.toFixed(decimals)) : fractions },
      ],
    };
  }

  async #getCombinedOrderRequest(): Promise<CombinedOrderRequest> {
    const { form, asset } = this.#cache.value;
    if (!form || !asset) {
      return null;
    }

    const isBuy = form.get('direction').value === 'buy';
    const orders = [];
    const marketOrder = await firstValueFrom(this.#tradeFacade.combinedMarketOrder$);
    marketOrder && orders.push({ quantity: marketOrder.quantity });

    const fractionalOrder = await firstValueFrom(this.#tradeFacade.combinedFractionalOrder$);
    fractionalOrder &&
      orders.push({
        quantity: isBuy ? null : fractionalOrder.quantity,
        fixedRemainingAmount: isBuy ? fractionalOrder.fixedRemainingAmount : null,
      });

    return {
      customerId: this.#dataService.getSelectedCustomer(),
      isin: asset.isin,
      direction: form.get('direction').value,
      orders,
    };
  }

  public placeOrder = async () => {
    const { form, asset } = this.#cache.value;
    if (!form || !asset) {
      throw new Error('Order could not be placed');
    }

    const orderRequest = this.#getOrderRequest();
    if (!orderRequest) {
      throw new Error('Order could not be placed');
    }

    this.#tradeFacade.placeOrder(orderRequest);
  };

  public placeCombinedOrder = async () => {
    const { form, asset } = this.#cache.value;
    if (!form || !asset) {
      throw new Error('Combined Order could not be placed');
    }

    const combinedOrderRequest = await this.#getCombinedOrderRequest();
    if (!combinedOrderRequest) {
      throw new Error('Combined Order could not be placed');
    }

    this.#tradeFacade.placeCombinedOrder(combinedOrderRequest);
  };

  public confirmLowTrading(): Promise<boolean> {
    return firstValueFrom(
      this.#http.post<boolean>(environment.apihost + environment.confirmLowTradingUrl, this.#dataService.getSelectedCustomer()),
    );
  }

  public confirmOtcTrading(): Promise<boolean> {
    return firstValueFrom(
      this.#http.post<boolean>(environment.apihost + environment.confirmOtcTradingUrl, this.#dataService.getSelectedCustomer()),
    );
  }

  public getNextExecDate = async (): Promise<Date> => {
    if (!this.#savingsPlanFacade?.loadExecDates) {
      return null;
    }
    this.#savingsPlanFacade.loadExecDates();

    return firstValueFrom(
      this.#savingsPlanFacade.nextExecDate$.pipe(
        filter((nextExecDate) => !!nextExecDate),
        map((nextExecDate) => new Date(nextExecDate)),
      ),
    );
  };

  #subscribeToPushQuote(isin: string): void {
    this.#pushQuoteService
      .subscribe(isin)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((quote) => this.updateQuote(quote));
  }

  #subscribeToOrderVolume(asset: Asset): void {
    this.orderVolume$
      .pipe(
        distinctUntilChanged(),
        switchMap((orderVolume) => this.#loadMinimumQuantitySurcharge(orderVolume, asset)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((minimumQuantitySurcharge) => this.#minimumQuantitySurcharge.next(minimumQuantitySurcharge));
  }

  #loadMinimumQuantitySurcharge(orderVolume: number, asset: Asset): Promise<MinimumQuantitySurcharge> {
    const quantity = Number(this.#cache?.value?.form?.get('quantityForm')?.get('quantity')?.value);
    return this.#minimumQuantitySurchargeService.determine(orderVolume, !isNaN(quantity) ? quantity : 0, asset);
  }

  #calculateOrderVolume(data: OrderVolumeCalculation): number {
    const { execType, quote, direction, quantity, stopPrice, stopLimitPrice, limitPrice, stopLoss } = data;

    let quote2use = 0;
    switch (execType) {
      case 'market':
        quote2use = direction === 'buy' ? quote.ask : quote.bid;
        break;
      case 'stop':
      case 'stoplimit':
        if (stopLimitPrice) {
          quote2use = stopLimitPrice;
        } else if (stopPrice) {
          quote2use = stopPrice;
        }
        break;
      case 'limit':
        quote2use = limitPrice;
        break;
      case 'oco':
        quote2use = stopLoss;
        break;
      case 'tsl':
        quote2use = this.#getTslQuote(data);
        break;
      default:
        throw new Error(`execType is not defined: ${execType}`);
    }
    return quote2use * quantity;
  }

  #getTslQuote(data: OrderVolumeCalculation): number {
    const { quote, trailingAbs, trailingRel, trailingType, initialStopPrice } = data;
    if (initialStopPrice) {
      return initialStopPrice;
    }

    if (!trailingAbs && !trailingRel) {
      return 0;
    }

    const { bid } = quote;
    const quote2use = trailingType === 'abs' ? bid - trailingAbs : bid - bid * (trailingRel / 100);
    return quote2use >= 0 ? quote2use : 0;
  }

  #getOrderVolume(data?: OrderVolumeCalculation): number {
    if (!data?.quote) {
      return 0;
    }

    const { quantity } = data;
    return !quantity || quantity < 0 || isNaN(quantity) ? 0 : this.#calculateOrderVolume(data);
  }

  public createQueryParams(order: OrderData | OrderPushData, shouldAddValidToDate = false): Params {
    const { isin, direction, quantity, execType, limitPrice, stopPrice, validToDate, trailingPriceAbs, trailingPricePct } = order;
    const params: Params = {
      isin,
      direction,
      quantity: formatNumber(quantity, 'en', '1.0-8'),
    };

    switch (execType) {
      case 'market':
        break;
      case 'limit':
        params['execType'] = execType;
        params['limitPrice'] = limitPrice || '';
        break;
      case 'stop':
        params['execType'] = execType;
        params['stopPrice'] = stopPrice || '';
        break;
      case 'stoplimit':
        params['execType'] = execType;
        params['stopPrice'] = stopPrice || '';
        params['stopLimitPrice'] = limitPrice || '';
        break;
      case 'oco':
        params['execType'] = execType;
        params['stopLoss'] = stopPrice || '';
        params['takeProfit'] = limitPrice || '';
        break;
      case 'tsl':
        params['execType'] = execType;
        params['initialStopPrice'] = stopPrice || '';
        params['trailingAbs'] = trailingPriceAbs || '';
        params['trailingRel'] = trailingPricePct || '';
        break;
      default:
        break;
    }

    if (shouldAddValidToDate && validToDate) {
      params['validity'] = validToDate;
    }

    return params;
  }
}
